/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable prettier/prettier */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import "react-phone-input-2/lib/style.css";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  CardContent,
} from "@mui/material";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import { useMutation } from "@apollo/client";
import DISCONNECT_TWILIO from "src/graphql/mutation/settings/disconnectTwilio";
import DELETE_TWILIO from "src/graphql/mutation/settings/deleteTwilio";
import DeleteDialog from "src/components/shared/atoms/dialog";
import Button from "src/components/shared/atoms/button";
import NoRecordFound from "src/components/shared/molecules/NoRecordFound";
import { useSnackbar } from "notistack";
import { TypesForPhoneNumber, PropsTwilioPhoneConfiguration } from "../Types";
import TwilioBtn from "./button";
import useStyle from "./Styles";

const TwilioPhoneConfiguration: React.FC<PropsTwilioPhoneConfiguration> = ({
  setIsAuth,
  twiliodata,
}) => {
  const {
    accountSID = "",
    authToken = "",
    phoneNumber = "",
  } = twiliodata || {};
  const [phnNumber, setPhnNumber] = useState<TypesForPhoneNumber[] | string>(
    phoneNumber,
  );
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const { classes } = useStyle();
  const { enqueueSnackbar } = useSnackbar();

  const [
    disconnectTwilioConfiguration,
    {
      data: disconnectTwilioConfigurationQueryResult,
      loading: disconnectTwilioConfigurationLoading,
    },
  ] = useMutation(DISCONNECT_TWILIO);

  const [
    deleteTwilioConfiguration,
    {
      data: deleteTwilioConfigurationQueryResult,
      loading: deleteTwilioConfigurationLoading,
    },
  ] = useMutation(DELETE_TWILIO);

  useEffect(() => {
    if (
      disconnectTwilioConfigurationQueryResult &&
      disconnectTwilioConfigurationQueryResult.disconnectTwilioConfiguration
    ) {
      if (
        disconnectTwilioConfigurationQueryResult.disconnectTwilioConfiguration
          .success
      ) {
        setIsAuth(false);
      } else {
        enqueueSnackbar(
          disconnectTwilioConfigurationQueryResult.disconnectTwilioConfiguration
            .error,
          {
            variant: "error",
          },
        );
      }
    }
  }, [disconnectTwilioConfigurationQueryResult]);

  const disconnectTwilioConf = async () => {
    try {
      await disconnectTwilioConfiguration({
        variables: {
          accountSID,
          authToken,
        },
      });
    } catch (err: any) {
      enqueueSnackbar("Something went wrong! Try again.", {
        variant: "error",
      });
    }
  };

  // Surface the delete response (success or backend error) the same way
  // disconnect does.
  useEffect(() => {
    if (
      deleteTwilioConfigurationQueryResult &&
      deleteTwilioConfigurationQueryResult.deleteTwilioConfiguration
    ) {
      const { success, error } =
        deleteTwilioConfigurationQueryResult.deleteTwilioConfiguration;
      if (success) {
        enqueueSnackbar(
          "Twilio configuration removed. The SID is now free to be claimed.",
          { variant: "success" },
        );
        setIsAuth(false);
      } else if (error) {
        enqueueSnackbar(error, { variant: "error" });
      }
    }
  }, [deleteTwilioConfigurationQueryResult]);

  // Deletion fully releases the SID for other organizations to claim,
  // so confirm via the shared DeleteDialog — distinct from a disconnect
  // which keeps ownership.
  const removeTwilioConf = async () => {
    try {
      await deleteTwilioConfiguration({
        variables: { accountSID },
      });
      setShowRemoveDialog(false);
    } catch (err: any) {
      enqueueSnackbar("Something went wrong! Try again.", {
        variant: "error",
      });
    }
  };

  return (
    <>
      <Typography className={classes.heading}>Twilio | Messages</Typography>
      <CardContent className={classes.cardContent}>
        <Box className={classes.buttonBox}>
          <Typography className={classes.subheader}>
            Status: <span className={classes.connected}>connected</span>
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              className={classes.disconnectButton}
              handleClick={disconnectTwilioConf}
              circular={disconnectTwilioConfigurationLoading}
            >
              Disconnect
            </Button>
            <Button
              variant="outlined"
              handleClick={() => setShowRemoveDialog(true)}
              circular={deleteTwilioConfigurationLoading}
            >
              Delete
            </Button>
          </Box>
        </Box>

        <TableContainer className={classes.tableContainer}>
          <Table className={classes.tableStyle}>
            <TableBody>
              {phnNumber && Array.isArray(phnNumber) && phnNumber.length > 0 ? (
                phnNumber.map((curr: TypesForPhoneNumber) => (
                  <TableRow key={curr.phoneNumber}>
                    <TableCell className={classes.rowCell}>
                      <Box className={classes.rowCellProfileBox}>
                        <PhoneIphoneIcon className={classes.phoneIcon} />
                        <Typography
                          component="p"
                          sx={{ display: "inline-block" }}
                          className={classes.numberText}
                        >
                          {curr.phoneNumber}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell className={classes.rowCell}>
                      <Box className={classes.rowCellControlBox}>
                        <TwilioBtn
                          phnNumber={phnNumber}
                          setPhnNumber={setPhnNumber}
                          data={curr}
                          accountSID={accountSID}
                          authToken={authToken}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <Box mt={5}>
                  <NoRecordFound />
                </Box>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
      <DeleteDialog
        open={showRemoveDialog}
        modalClose={() => setShowRemoveDialog(false)}
        actionHandler={removeTwilioConf}
        loader={deleteTwilioConfigurationLoading}
        btnText="Delete"
        text="Removing these Twilio credentials will release the Account SID so another Kuikwit account can claim it. You'll need to re-add and re-verify if you want to use it here again."
      />
    </>
  );
};

export default TwilioPhoneConfiguration;
